<script setup lang="ts">
  defineOptions({
    name: 'ChatMessageList',
  })

  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type PropType } from 'vue'

  import ChatDateDivider from '../atoms/ChatDateDivider.vue'

  import ChatMessageBubble from '../atoms/ChatMessageBubble.vue'

  import { useChatParticipant } from '../../composables/useChatParticipant'

  import { createResolveNextChatVoiceAutoplayKey, getChatVoicePlaybackKey } from '../../utils/chat-voice-autoplay.utils'

  import type { IConversationRecipient, IMessage } from '../../types/conversation.types'

  const props = defineProps({
    messages: {
      type: Array as PropType<IMessage[]>,
      default: () => [],
    },
    recipient: {
      type: Object as PropType<IConversationRecipient | null>,
      default: null,
    },
    isLoadingMore: {
      type: Boolean,
      default: false,
    },
    hasMoreMessages: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits<{
    loadMore: []
  }>()

  const { isMessageFromCurrentUser } = useChatParticipant()

  // Refs for infinite scrolling
  const scrollContainerRef = ref<HTMLElement | null>(null)

  const loadMoreTriggerRef = ref<HTMLElement | null>(null)

  const hasInitialized = ref(false)

  const initTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

  const intersectionObserver = ref<IntersectionObserver | null>(null)

  // Group messages by date
  const groupedMessages = computed(() => {
    if (!props.messages || props.messages.length === 0) return []

    const groups: {
      date: string
      messages: Array<IMessage & { showTime: boolean; showAvatar: boolean }>
    }[] = []
    let currentDate = ''
    let currentGroup: Array<IMessage & { showTime: boolean; showAvatar: boolean }> = []

    // Process messages (they come in reverse chronological order - newest first)
    for (let i = 0; i < props.messages.length; i++) {
      const message = props.messages[i]
      const messageDate = formatDateKey(message.createdAt)

      // Start new group if date changes
      if (messageDate !== currentDate) {
        if (currentGroup.length > 0) {
          groups.push({ date: currentDate, messages: currentGroup })
        }
        currentDate = messageDate
        currentGroup = []
      }

      // Determine if we should show time (if sender changes or >5 min gap)
      const prevMessage = props.messages[i - 1]
      const showTime =
        !prevMessage ||
        prevMessage.sender !== message.sender ||
        Math.abs(new Date(prevMessage.createdAt).getTime() - new Date(message.createdAt).getTime()) > 5 * 60 * 1000

      // Determine if we should show avatar (newest message in consecutive group)
      const prevMessageForAvatar = props.messages[i - 1]
      const showAvatar =
        !prevMessageForAvatar ||
        prevMessageForAvatar.sender !== message.sender ||
        formatDateKey(prevMessageForAvatar.createdAt) !== messageDate

      currentGroup.push({
        ...message,
        showTime,
        showAvatar,
      })
    }

    // Push the last group
    if (currentGroup.length > 0) {
      groups.push({ date: currentDate, messages: currentGroup })
    }

    return groups
  })

  const toLocalDateKey = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const getMessageRenderKey = (message: IMessage, index: number) => {
    // Prefer client-stable refs so ACK (_id swap) does not remount and kill <audio> playback.
    return getChatVoicePlaybackKey(message) || `${message.sender}-${message.createdAt}-${index}`
  }

  // Setup intersection observer for reverse infinite scroll
  const setupIntersectionObserver = () => {
    // Don't setup if we're already loading or no more messages
    if (!scrollContainerRef.value || !loadMoreTriggerRef.value) return
    if (!props.hasMoreMessages) return

    if (!process.client) return

    intersectionObserver.value?.disconnect()
    intersectionObserver.value = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && props.hasMoreMessages && !props.isLoadingMore && hasInitialized.value) {
          emit('loadMore')
        }
      },
      {
        root: scrollContainerRef.value,
        rootMargin: '80px 0px 0px 0px',
        threshold: 0,
      }
    )

    intersectionObserver.value.observe(loadMoreTriggerRef.value)
  }

  // Scroll to bottom (scrollTop = 0 due to flex-col-reverse)
  const scrollToBottom = () => {
    if (!scrollContainerRef.value) return
    scrollContainerRef.value.scrollTop = 0
  }

  onMounted(() => {
    // Delay initialization to prevent immediate trigger
    initTimeout.value = setTimeout(() => {
      hasInitialized.value = true
      setupIntersectionObserver()
    }, 500)
  })

  onBeforeUnmount(() => {
    if (initTimeout.value) {
      clearTimeout(initTimeout.value)
    }
    intersectionObserver.value?.disconnect()
  })

  // Helper to format date as key in local timezone.
  function formatDateKey(dateString: string): string {
    const now = new Date()
    if (!dateString) return toLocalDateKey(now)

    let parsedDate: Date
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number)
      parsedDate = new Date(year, month - 1, day)
    } else {
      parsedDate = new Date(dateString)
    }

    if (Number.isNaN(parsedDate.getTime())) {
      return toLocalDateKey(now)
    }

    return toLocalDateKey(parsedDate)
  }

  defineExpose({
    scrollToBottom,
  })

  // Watch for loading state changes to re-register observer after loading completes
  watch(
    () => props.isLoadingMore,
    (isLoading, wasLoading) => {
      // When loading finishes, re-register the observer
      if (wasLoading && !isLoading) {
        nextTick(() => {
          setupIntersectionObserver()
        })
      }
    }
  )

  // Watch for hasMoreMessages to cleanup observer when no more messages
  watch(
    () => props.hasMoreMessages,
    hasMore => {
      if (!hasMore) {
        intersectionObserver.value?.disconnect()
        return
      }

      nextTick(() => {
        setupIntersectionObserver()
      })
    }
  )
</script>

<template>
  <div ref="scrollContainerRef" class="flex flex-1 flex-col-reverse overflow-y-auto pb-3">
    <!-- Messages grouped by date (flex-col-reverse puts newest at bottom) -->
    <div v-for="group in groupedMessages" :key="group.date" class="flex w-full flex-col-reverse">
      <!-- Messages in group -->
      <div class="flex w-full flex-col-reverse gap-1 px-3">
        <div v-for="(message, index) in group.messages" :key="getMessageRenderKey(message, index)" class="w-full">
          <ChatMessageBubble
            :message="message"
            :is-sent="isMessageFromCurrentUser(message)"
            :show-avatar="message.showAvatar && !isMessageFromCurrentUser(message)"
            :show-time="message.showTime"
            :recipient="recipient"
            :resolve-next-autoplay-key="createResolveNextChatVoiceAutoplayKey(() => messages, message)"
          />
        </div>
      </div>

      <!-- Date Divider -->
      <ChatDateDivider :date="group.date" />
    </div>

    <!-- Load more trigger (at the visual top due to flex-col-reverse) -->
    <div v-if="hasMoreMessages && !isLoadingMore" id="chat-message-list-load-more-trigger" ref="loadMoreTriggerRef" class="h-1 w-full" />
  </div>
</template>
